import { Tooltip } from '@chakra-ui/react'
import type { NavItemsState } from '@core/app/types/applyV2Type'
import useApplyV2NavTranslations from '@core/utils/hooks/useApplyV2NavTranslations'
import clsx from 'clsx'
import React, { useEffect, useState } from 'react'
import './ApplyV2Accordion.scss'

const baseAccordionClass = `ApplyV2Accordion`

interface ApplyV2AccordionProps {
    className?: string
    isExpanded?: boolean
    defaultExpanded?: boolean
    isLoading?: boolean
    status?: 'loading' | 'error' | 'success' | 'default'
    children?: React.ReactNode
    onToggle?: (value: boolean) => void
    title?: React.ReactNode
    icon?: React.ReactNode
    fieldState?: NavItemsState
    isShowExpandButton?: boolean
}

function ApplyV2Accordion({
    className = '',
    title = '',
    children,
    status = 'default',
    isExpanded = true,
    isLoading = false,
    onToggle = () => {},
    defaultExpanded = true,
    icon,
    fieldState,
    isShowExpandButton = true,
}: ApplyV2AccordionProps) {
    const { getTitle } = useApplyV2NavTranslations()
    const [expanded, setExpanded] = useState(isExpanded)

    useEffect(() => {
        setExpanded(isExpanded)
    }, [isExpanded])

    const toggleExpanded = () => {
        setExpanded(!expanded)
        onToggle(!expanded)
    }

    return (
        <div
            className={clsx(baseAccordionClass, className, {
                [`${baseAccordionClass}--expanded`]: expanded,
                [`${baseAccordionClass}--loading`]: isLoading,
                [`${baseAccordionClass}--status-${status}`]: status !== 'default',
                [`${className}--expanded`]: className && expanded,
                [`${className}--loading`]: className && isLoading,
                [`${className}--status-${status}`]: className && status !== 'default',
            })}
        >
            <div className={`${baseAccordionClass}__header`}>
                {icon && <div className={`${baseAccordionClass}__header__icon`}>{icon}</div>}
                <div
                    className={clsx(
                        `${baseAccordionClass}__header__title ${baseAccordionClass}__header__title-${fieldState}`
                    )}
                >
                    {title}
                </div>
                {isShowExpandButton && (
                    <Tooltip
                        hasArrow
                        isDisabled={isLoading}
                        openDelay={500}
                        label={expanded ? getTitle('7837', 'Collapse') : getTitle('6939', 'Expand')}
                        // placement={'top'}
                        padding={'8px 16px'}
                        borderRadius={'4px'}
                        backgroundColor={'#19191A'}
                        fontWeight={600}
                        fontSize={'12px'}
                        shouldWrapChildren
                    >
                        <button
                            className={`${baseAccordionClass}__header__toggle`}
                            onClick={() => toggleExpanded()}
                            disabled={isLoading}
                            type="button"
                        >
                            {expanded ? (
                                <i className="fa-regular fa-chevron-up"></i>
                            ) : (
                                <i className="fa-regular fa-chevron-down"></i>
                            )}
                        </button>
                    </Tooltip>
                )}
            </div>
            {/*<div className={`${baseAccordionClass}__content`}>{children}</div>*/}
            <div
                className={clsx(`${baseAccordionClass}__content`, {
                    [`${baseAccordionClass}__content--expanded`]: expanded,
                    [`${className}__content`]: className,
                    [`${className}__content--expanded`]: className && expanded,
                })}
            >
                <div
                    className={clsx(`${baseAccordionClass}__content__body`, {
                        [`${baseAccordionClass}__content__body--expanded`]: expanded,
                        [`${className}__content__body`]: className,
                        [`${className}__content__body--expanded`]: className && expanded,
                    })}
                >
                    {children}
                </div>
            </div>
        </div>
    )
}

export default ApplyV2Accordion
